import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {Context} from "../../index";
import ItemCreation from "./ItemCreation";
import ItemUpdate from "./ItemUpdate";
import {fetchItemsPage} from "../../http/itemsAPI";
import {fetchOrdersAdmin, fetchOrdersStats} from "../../http/ordersAPI";
import {$authHost} from "../../http";
import {
    ADMIN_LIST_PAGE_SIZE,
    ADMIN_ORDERS_PAGE_SIZE,
    ORDER_DELIVERY_YANDEX_PICKUP,
    ORDER_STATUS_FILTER_OPTIONS,
    orderDeliveryPresentation,
    orderStatusPresentation,
} from "../../util/Constants";
import {catalogItemId} from "../../util/catalogItemId";
import {ITEMS_ID} from "../../http/ApiEndpoints";
import {createCategory, deleteCategory, fetchCategories, updateCategory} from "../../http/categoriesAPI";
import {useDebounced} from "../../hooks/useDebounced";
import {formatOrderDate} from "../../util/formatOrderDate";
import {
    endOfDayLocal,
    firstDayOfCurrentMonth,
    firstDayOfPreviousMonth,
    lastDayOfPreviousMonth,
    startOfDayLocal,
    toDateInputValue,
    today,
} from "../../util/dateRange";

const ORDER_SORT_OPTIONS = [
    {value: 'dateTime,desc', label: 'Сначала новые'},
    {value: 'dateTime,asc', label: 'Сначала старые'},
    {value: 'price,desc', label: 'Сумма: больше'},
    {value: 'price,asc', label: 'Сумма: меньше'},
    {value: 'number,asc', label: 'Номер А→Я'},
    {value: 'number,desc', label: 'Номер Я→А'},
];

const ITEM_SORT_OPTIONS = [
    {value: 'title,asc', label: 'Название А→Я'},
    {value: 'title,desc', label: 'Название Я→А'},
    {value: 'id,desc', label: 'ID ↓ новые'},
    {value: 'id,asc', label: 'ID ↑ старые'},
    {value: 'price,asc', label: 'Цена ↑'},
    {value: 'price,desc', label: 'Цена ↓'},
    {value: 'count,asc', label: 'Остаток ↑'},
    {value: 'count,desc', label: 'Остаток ↓'},
];

const Admin = () => {
    const {items: itemsStore} = useContext(Context);
    const [itemForm, setItemForm] = useState(false);
    const [items, setItems] = useState([]);
    const [orders, setOrders] = useState([]);
    const [categories, setCategories] = useState([]);
    const [stats, setStats] = useState({totalOrders: 0, totalRevenue: '0.00', pendingOrders: 0});
    const [statsLoading, setStatsLoading] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [newCategoryTitle, setNewCategoryTitle] = useState('');
    const [newCategoryDisplayOrder, setNewCategoryDisplayOrder] = useState('');
    const [editingItem, setEditingItem] = useState(null);
    const [editingCategory, setEditingCategory] = useState(null);
    const [error, setError] = useState('');
    const [tab, setTab] = useState('orders');
    const [orderSearch, setOrderSearch] = useState('');
    const [orderSort, setOrderSort] = useState('dateTime,desc');
    const [orderStatusFilter, setOrderStatusFilter] = useState('');
    const [orderPage, setOrderPage] = useState(0);
    const [orderTotalPages, setOrderTotalPages] = useState(0);
    const [orderTotalElements, setOrderTotalElements] = useState(0);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const debouncedOrderSearch = useDebounced(orderSearch, 350);
    const orderFilterKeyRef = useRef('');
    const [ordersReloadNonce, setOrdersReloadNonce] = useState(0);
    const defaultStatsRange = useMemo(() => ({
        start: toDateInputValue(firstDayOfCurrentMonth()),
        end: toDateInputValue(today()),
    }), []);
    const previousMonthRange = useMemo(() => ({
        start: toDateInputValue(firstDayOfPreviousMonth()),
        end: toDateInputValue(lastDayOfPreviousMonth()),
    }), []);
    const [statsStart, setStatsStart] = useState(defaultStatsRange.start);
    const [statsEnd, setStatsEnd] = useState(defaultStatsRange.end);
    const [statsRangeError, setStatsRangeError] = useState('');
    const [statsReloadNonce, setStatsReloadNonce] = useState(0);
    const [itemPage, setItemPage] = useState(0);
    const [itemTotalPages, setItemTotalPages] = useState(0);
    const [itemTotalElements, setItemTotalElements] = useState(0);
    const [itemSort, setItemSort] = useState('title,asc');
    const [itemSearch, setItemSearch] = useState('');
    const [itemCategoryFilter, setItemCategoryFilter] = useState('');
    const [itemStockFilter, setItemStockFilter] = useState('all');
    const debouncedItemSearch = useDebounced(itemSearch, 350);
    const itemFilterKeyRef = useRef('');
    const [itemsReloadNonce, setItemsReloadNonce] = useState(0);
    const navigate = useNavigate();

    const applyCategoriesList = useCallback(
        (data) => {
            const list = data || [];
            setCategories(list);
            itemsStore.setCategories(list);
        },
        [itemsStore],
    );

    useEffect(() => {
        fetchCategories().then(applyCategoriesList);
    }, [applyCategoriesList]);

    useEffect(() => {
        const filterKey = `${debouncedOrderSearch}|${orderStatusFilter}|${orderSort}`;
        let pageToFetch = orderPage;
        if (orderFilterKeyRef.current !== filterKey) {
            orderFilterKeyRef.current = filterKey;
            if (orderPage !== 0) {
                setOrderPage(0);
                return;
            }
            pageToFetch = 0;
        }

        const params = {
            page: pageToFetch,
            size: ADMIN_ORDERS_PAGE_SIZE,
            sort: orderSort,
        };
        const q = debouncedOrderSearch.trim();
        if (q) params.query = q;
        if (orderStatusFilter) params.status = orderStatusFilter;

        setOrdersLoading(true);
        fetchOrdersAdmin(params)
            .then((data) => {
                setOrders(data.orders || []);
                setOrderTotalPages(data.totalPages != null ? data.totalPages : 0);
                setOrderTotalElements(
                    data.totalElements != null ? data.totalElements : (data.orders || []).length,
                );
            })
            .catch(() => {
                setOrders([]);
                setOrderTotalPages(0);
                setOrderTotalElements(0);
            })
            .finally(() => setOrdersLoading(false));
    }, [orderPage, orderSort, debouncedOrderSearch, orderStatusFilter, ordersReloadNonce]);

    useEffect(() => {
        if (statsStart && statsEnd && statsStart > statsEnd) {
            setStatsRangeError('Дата начала позже даты окончания');
            return;
        }
        setStatsRangeError('');
        const params = {};
        if (statsStart) params.startDate = startOfDayLocal(statsStart);
        if (statsEnd) params.endDate = endOfDayLocal(statsEnd);

        setStatsLoading(true);
        fetchOrdersStats(params)
            .then((data) => {
                const revenue = data?.totalRevenue;
                const revenueStr = revenue != null
                    ? Number(revenue).toFixed(2)
                    : '0.00';
                setStats({
                    totalOrders: data?.totalOrders ?? 0,
                    totalRevenue: revenueStr,
                    pendingOrders: data?.pendingOrders ?? 0,
                });
            })
            .catch(() => {
                setStats({totalOrders: 0, totalRevenue: '0.00', pendingOrders: 0});
            })
            .finally(() => setStatsLoading(false));
    }, [statsStart, statsEnd, statsReloadNonce]);

    useEffect(() => {
        if (tab !== 'items') {
            return;
        }
        const filterKey = `${debouncedItemSearch}|${itemCategoryFilter}|${itemStockFilter}|${itemSort}`;
        let pageToFetch = itemPage;

        if (itemFilterKeyRef.current !== filterKey) {
            itemFilterKeyRef.current = filterKey;
            if (itemPage !== 0) {
                setItemPage(0);
                return;
            }
            pageToFetch = 0;
        }

        const params = {
            page: pageToFetch,
            size: ADMIN_LIST_PAGE_SIZE,
            sort: itemSort,
            filtered: false,
        };
        const q = debouncedItemSearch.trim();
        if (q) {
            params.query = q;
        }
        if (itemCategoryFilter) {
            params.category = itemCategoryFilter;
        }
        if (itemStockFilter === 'in') {
            params.onlyAvailable = true;
        }
        if (itemStockFilter === 'out') {
            params.maxStock = 0;
        }
        fetchItemsPage(params)
            .then((data) => {
                setItems(data.items || []);
                setItemTotalPages(data.totalPages != null ? data.totalPages : 0);
                setItemTotalElements(
                    data.totalElements != null ? data.totalElements : (data.items || []).length,
                );
            })
            .catch(() => {
                setItems([]);
                setItemTotalPages(0);
                setItemTotalElements(0);
            });
    }, [
        tab,
        itemPage,
        itemSort,
        debouncedItemSearch,
        itemCategoryFilter,
        itemStockFilter,
        itemsReloadNonce,
    ]);

    const loadData = () => {
        setItemsReloadNonce((n) => n + 1);
        fetchCategories().then(applyCategoriesList);
        setOrdersReloadNonce((n) => n + 1);
        setStatsReloadNonce((n) => n + 1);
    };

    const handleDeleteItem = async (itemId) => {
        if (itemId == null) {
            alert('Не удалось определить id товара');
            return;
        }
        if (window.confirm('Удалить товар?')) {
            try {
                await $authHost.delete(ITEMS_ID + itemId);
                loadData();
            } catch (err) {
                alert(err.response?.data?.message || 'Ошибка удаления');
            }
        }
    };

    const parseDisplayOrder = (raw) => {
        const trimmed = String(raw ?? '').trim();
        if (trimmed === '') return null;
        const n = Number(trimmed);
        return Number.isInteger(n) ? n : NaN;
    };

    const handleUpdateCategory = async () => {
        if (!editingCategory?.id) return;
        if (!newCategoryTitle.trim()) {
            setError('Название категории не может быть пустым');
            return;
        }
        const orderValue = parseDisplayOrder(newCategoryDisplayOrder);
        if (Number.isNaN(orderValue)) {
            setError('Порядок показа должен быть целым числом');
            return;
        }
        try {
            await updateCategory(editingCategory.id, newCategoryTitle.trim(), orderValue);
            setEditingCategory(null);
            setNewCategoryTitle('');
            setNewCategoryDisplayOrder('');
            setError('');
            loadData();
        } catch (err) {
            setError(err.response?.data?.message || 'Ошибка сохранения категории');
        }
    };

    const handleCreateCategory = async () => {
        if (!newCategoryTitle.trim()) {
            setError('Название категории не может быть пустым');
            return;
        }
        const orderValue = parseDisplayOrder(newCategoryDisplayOrder);
        if (Number.isNaN(orderValue)) {
            setError('Порядок показа должен быть целым числом');
            return;
        }
        try {
            await createCategory(newCategoryTitle, orderValue);
            setNewCategoryTitle('');
            setNewCategoryDisplayOrder('');
            setShowCategoryModal(false);
            setError('');
            loadData();
        } catch (err) {
            setError(err.response?.data?.message || 'Ошибка создания категории');
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        if (window.confirm('Удалить категорию? Все товары этой категории останутся без категории.')) {
            try {
                await deleteCategory(categoryId);
                loadData();
            } catch (err) {
                alert(err.response?.data?.message || 'Ошибка удаления категории');
            }
        }
    };

    const tabBtn = (key, label) => (
        <button
            type="button"
            onClick={() => setTab(key)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                tab === key
                    ? 'bg-pine-800 text-white shadow-sm'
                    : 'text-bark-600 hover:bg-cream-200/80'
            }`}
        >
            {label}
        </button>
    );

    const tableWrap = (children) => (
        <div className="overflow-x-auto rounded-2xl border border-stone-200/70">
            <table className="min-w-full divide-y divide-stone-200 text-sm">
                {children}
            </table>
        </div>
    );

    return (
        <div>
            <h1 className="text-3xl font-semibold tracking-tight text-bark-900">Панель администратора</h1>

            <section className="mt-8 rounded-2xl border border-stone-200/70 bg-cream-50/60 p-4 sm:p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-bark-700">
                        Статистика за период
                    </h2>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <label className="flex items-center gap-2 text-sm text-bark-700" htmlFor="admin-stats-start">
                            <span>С</span>
                            <input
                                id="admin-stats-start"
                                type="date"
                                className="lut-input min-h-10 text-sm"
                                value={statsStart}
                                max={statsEnd || undefined}
                                onChange={(e) => setStatsStart(e.target.value)}
                            />
                        </label>
                        <label className="flex items-center gap-2 text-sm text-bark-700" htmlFor="admin-stats-end">
                            <span>по</span>
                            <input
                                id="admin-stats-end"
                                type="date"
                                className="lut-input min-h-10 text-sm"
                                value={statsEnd}
                                min={statsStart || undefined}
                                onChange={(e) => setStatsEnd(e.target.value)}
                            />
                        </label>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                className="lut-btn-ghost min-h-10"
                                onClick={() => {
                                    setStatsStart(defaultStatsRange.start);
                                    setStatsEnd(defaultStatsRange.end);
                                }}
                            >
                                Текущий месяц
                            </button>
                            <button
                                type="button"
                                className="lut-btn-ghost min-h-10"
                                onClick={() => {
                                    setStatsStart(previousMonthRange.start);
                                    setStatsEnd(previousMonthRange.end);
                                }}
                            >
                                Прошлый месяц
                            </button>
                        </div>
                    </div>
                </div>
                {statsRangeError && (
                    <p className="mt-2 text-sm text-red-600">{statsRangeError}</p>
                )}

                <div className={`mt-5 grid gap-4 sm:grid-cols-3 ${statsLoading ? 'opacity-60' : ''}`}>
                    <div className="lut-card py-5 text-center">
                        <p className="text-xs font-medium uppercase tracking-wide text-bark-500">Всего заказов</p>
                        <p className="mt-2 text-3xl font-semibold text-pine-900">{stats.totalOrders}</p>
                    </div>
                    <div className="lut-card py-5 text-center">
                        <p className="text-xs font-medium uppercase tracking-wide text-bark-500">Выручка</p>
                        <p className="mt-2 text-3xl font-semibold text-pine-900">{stats.totalRevenue} ₽</p>
                    </div>
                    <div className="lut-card py-5 text-center">
                        <p className="text-xs font-medium uppercase tracking-wide text-bark-500">Ожидают оплаты</p>
                        <p className="mt-2">
                            <span className="inline-flex rounded-full bg-mustard-200 px-3 py-1 text-lg font-semibold text-bark-900">
                                {stats.pendingOrders}
                            </span>
                        </p>
                    </div>
                </div>
            </section>

            <div className="mt-8 flex flex-wrap gap-2 rounded-2xl border border-stone-200/60 bg-cream-100/50 p-1">
                {tabBtn('orders', 'Заказы')}
                {tabBtn('items', 'Товары')}
                {tabBtn('categories', 'Категории')}
            </div>

            <div className="mt-6">
                {tab === 'orders' && (
                    <>
                        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
                            <div className="min-w-0 flex-1 sm:max-w-md">
                                <label className="lut-label" htmlFor="admin-order-search">
                                    Поиск: номер заказа, email, имя или телефон
                                </label>
                                <input
                                    id="admin-order-search"
                                    type="search"
                                    className="lut-input mt-1 min-h-10 text-sm"
                                    placeholder="Например, ivan@ или фрагмент имени"
                                    value={orderSearch}
                                    onChange={(e) => setOrderSearch(e.target.value)}
                                    autoComplete="off"
                                />
                            </div>
                            <div className="w-full shrink-0 sm:w-auto">
                                <label className="lut-label" htmlFor="admin-order-status">
                                    Статус
                                </label>
                                <select
                                    id="admin-order-status"
                                    className="lut-select-native mt-1 w-full min-w-0 sm:min-w-[11rem]"
                                    value={orderStatusFilter}
                                    onChange={(e) => setOrderStatusFilter(e.target.value)}
                                >
                                    {ORDER_STATUS_FILTER_OPTIONS.map((o) => (
                                        <option key={o.value || 'all'} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="w-full shrink-0 sm:w-auto">
                                <label className="lut-label" htmlFor="admin-order-sort">
                                    Сортировка
                                </label>
                                <select
                                    id="admin-order-sort"
                                    className="lut-select-native mt-1 w-full min-w-0 sm:min-w-[11rem]"
                                    value={orderSort}
                                    onChange={(e) => setOrderSort(e.target.value)}
                                >
                                    {ORDER_SORT_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="mb-3 text-sm text-bark-500">
                            Найдено по фильтру:{' '}
                            <span className="font-semibold text-bark-800">{orderTotalElements}</span>
                        </div>
                        {tableWrap(
                            <>
                                <thead className="bg-cream-100/80 text-left text-xs font-semibold uppercase tracking-wide text-bark-600">
                                    <tr>
                                        <th className="px-4 py-3">Номер</th>
                                        <th className="px-4 py-3">Дата</th>
                                        <th className="px-4 py-3">ФИО</th>
                                        <th className="px-4 py-3">Логин (email)</th>
                                        <th className="px-4 py-3">Доставка</th>
                                        <th className="px-4 py-3">Сумма</th>
                                        <th className="px-4 py-3">Статус</th>
                                        <th className="px-4 py-3">Действия</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y divide-stone-100 bg-white ${ordersLoading ? 'opacity-60' : ''}`}>
                                    {orders.length === 0 ? (
                                        <tr>
                                            <td
                                                className="px-4 py-8 text-center text-bark-500"
                                                colSpan={8}
                                            >
                                                {ordersLoading ? 'Загрузка…' : 'Заказов не найдено.'}
                                            </td>
                                        </tr>
                                    ) : (
                                        orders.map((order, idx) => {
                                            const st = orderStatusPresentation(order.status);
                                            const name = order.customerName?.trim() || '—';
                                            const login = order.customerEmail?.trim() || '—';
                                            const dtp = orderDeliveryPresentation(order.deliveryType);
                                            const dFee =
                                                order.deliveryType === ORDER_DELIVERY_YANDEX_PICKUP &&
                                                Number(order.deliveryFee) > 0
                                                    ? `${order.deliveryFee} ₽`
                                                    : '';
                                            return (
                                                <tr key={order.id ?? `order-${idx}`}>
                                                    <td className="whitespace-nowrap px-4 py-3 font-mono text-bark-800">
                                                        {order.number}
                                                    </td>
                                                    <td className="whitespace-nowrap px-4 py-3 text-bark-600">
                                                        {formatOrderDate(order.dateTime)}
                                                    </td>
                                                    <td className="max-w-[10rem] px-4 py-3 text-bark-800">
                                                        <span className="line-clamp-2" title={name}>
                                                            {name}
                                                        </span>
                                                    </td>
                                                    <td className="max-w-[12rem] px-4 py-3 text-bark-700">
                                                        <span className="line-clamp-2 break-all" title={login}>
                                                            {login}
                                                        </span>
                                                    </td>
                                                    <td className="max-w-[7rem] px-4 py-3 text-sm text-bark-700">
                                                        {order.deliveryType ? (
                                                            <>
                                                                {dtp.short}
                                                                {dFee ? (
                                                                    <span className="mt-0.5 block text-xs text-bark-500">
                                                                        {dFee}
                                                                    </span>
                                                                ) : null}
                                                            </>
                                                        ) : (
                                                            '—'
                                                        )}
                                                    </td>
                                                    <td className="whitespace-nowrap px-4 py-3 tabular-nums">
                                                        {order.price} ₽
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className={st.tw}>{st.translation}</span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate(`/orders/${order.id}`)}
                                                            className="text-sm font-medium text-pine-700 hover:underline"
                                                        >
                                                            Подробнее
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </>,
                        )}
                        {orderTotalPages > 1 && (
                            <div className="mt-8 flex justify-center gap-3">
                                <button
                                    type="button"
                                    disabled={orderPage <= 0}
                                    onClick={() => setOrderPage((p) => Math.max(0, p - 1))}
                                    className="lut-btn-ghost disabled:opacity-40"
                                >
                                    Назад
                                </button>
                                <span className="flex items-center text-sm text-bark-600">
                                    {orderPage + 1} / {orderTotalPages}
                                </span>
                                <button
                                    type="button"
                                    disabled={orderPage >= orderTotalPages - 1}
                                    onClick={() => setOrderPage((p) => p + 1)}
                                    className="lut-btn-ghost disabled:opacity-40"
                                >
                                    Вперёд
                                </button>
                            </div>
                        )}
                    </>
                )}

                {tab === 'items' && (
                    <>
                        <div className="mb-5 flex flex-col gap-3">
                            <div className="flex flex-wrap items-center gap-3">
                                <button type="button" onClick={() => setItemForm(true)} className="lut-btn-pine">
                                    + Добавить товар
                                </button>
                                <span className="text-sm text-bark-500">
                                    Всего по фильтру:{' '}
                                    <span className="font-semibold text-bark-800">{itemTotalElements}</span>
                                </span>
                            </div>
                            <div className="flex flex-col gap-3 rounded-2xl border border-stone-200/60 bg-cream-50/40 p-3 sm:p-4 lg:flex-row lg:flex-wrap lg:items-end">
                                <div className="min-w-0 flex-1 basis-full lg:basis-56 lg:min-w-[12rem]">
                                    <label className="lut-label" htmlFor="admin-item-search">
                                        Поиск по названию
                                    </label>
                                    <input
                                        id="admin-item-search"
                                        type="search"
                                        value={itemSearch}
                                        onChange={(e) => setItemSearch(e.target.value)}
                                        placeholder="Фрагмент названия"
                                        className="mt-1 w-full rounded-xl border border-stone-200/90 bg-white px-3 py-2 text-sm text-bark-800 shadow-sm outline-none focus:border-pine-500 focus:ring-2 focus:ring-pine-500/20"
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="w-full min-w-0 sm:flex-1 sm:basis-[8.5rem] lg:max-w-[11rem]">
                                    <label className="lut-label" htmlFor="admin-item-category">
                                        Категория
                                    </label>
                                    <select
                                        id="admin-item-category"
                                        className="lut-select-native mt-1 w-full"
                                        value={itemCategoryFilter}
                                        onChange={(e) => setItemCategoryFilter(e.target.value)}
                                    >
                                        <option value="">Все</option>
                                        {categories.map((c) => (
                                            <option key={c.id ?? c.title} value={c.title}>
                                                {c.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="w-full min-w-0 sm:flex-1 sm:basis-[8rem] lg:max-w-[10rem]">
                                    <label className="lut-label" htmlFor="admin-item-stock">
                                        Остаток
                                    </label>
                                    <select
                                        id="admin-item-stock"
                                        className="lut-select-native mt-1 w-full"
                                        value={itemStockFilter}
                                        onChange={(e) => setItemStockFilter(e.target.value)}
                                    >
                                        <option value="all">Все</option>
                                        <option value="in">С остатком</option>
                                        <option value="out">Нет в наличии</option>
                                    </select>
                                </div>
                                <div className="w-full min-w-0 sm:flex-1 sm:basis-[10rem] lg:max-w-[12rem]">
                                    <label className="lut-label" htmlFor="admin-item-sort">
                                        Сортировка
                                    </label>
                                    <select
                                        id="admin-item-sort"
                                        className="lut-select-native mt-1 w-full"
                                        value={itemSort}
                                        onChange={(e) => setItemSort(e.target.value)}
                                    >
                                        {ITEM_SORT_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        {tableWrap(
                            <>
                                <thead className="bg-cream-100/80 text-left text-xs font-semibold uppercase tracking-wide text-bark-600">
                                    <tr>
                                        <th className="px-4 py-3">ID</th>
                                        <th className="px-4 py-3">Название</th>
                                        <th className="px-4 py-3">Цена</th>
                                        <th className="px-4 py-3">Остаток</th>
                                        <th className="px-4 py-3">Категория</th>
                                        <th className="px-4 py-3">Действия</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-stone-100 bg-white">
                                    {items.length === 0 ? (
                                        <tr>
                                            <td
                                                className="px-4 py-8 text-center text-bark-500"
                                                colSpan={6}
                                            >
                                                Товары не найдены.
                                            </td>
                                        </tr>
                                    ) : (
                                        items.map((item, idx) => {
                                            const iid = catalogItemId(item);
                                            return (
                                                <tr key={iid != null ? iid : `item-${idx}`}>
                                                    <td className="px-4 py-3">{iid}</td>
                                                    <td className="px-4 py-3 font-medium text-bark-900">{item.title}</td>
                                                    <td className="px-4 py-3">{item.price} ₽</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                                            item.count > 10 ? 'bg-pine-100 text-pine-900' :
                                                                item.count > 0 ? 'bg-mustard-200 text-bark-900' : 'bg-red-100 text-red-800'
                                                        }`}>
                                                            {item.count}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-bark-600">{item.categoryResponse?.title}</td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex flex-wrap gap-3">
                                                            <button
                                                                type="button"
                                                                onClick={() => setEditingItem(item)}
                                                                className="text-sm font-medium text-pine-700 hover:underline"
                                                            >
                                                                Изменить
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDeleteItem(iid)}
                                                                className="text-sm text-red-700 hover:underline"
                                                            >
                                                                Удалить
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </>
                        )}
                        {itemTotalPages > 1 && (
                            <div className="mt-8 flex justify-center gap-3">
                                <button
                                    type="button"
                                    disabled={itemPage <= 0}
                                    onClick={() => setItemPage((p) => Math.max(0, p - 1))}
                                    className="lut-btn-ghost disabled:opacity-40"
                                >
                                    Назад
                                </button>
                                <span className="flex items-center text-sm text-bark-600">
                                    {itemPage + 1} / {itemTotalPages}
                                </span>
                                <button
                                    type="button"
                                    disabled={itemPage >= itemTotalPages - 1}
                                    onClick={() => setItemPage((p) => p + 1)}
                                    className="lut-btn-ghost disabled:opacity-40"
                                >
                                    Вперёд
                                </button>
                            </div>
                        )}
                    </>
                )}

                {tab === 'categories' && (
                    <>
                        <div className="mb-4">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowCategoryModal(true);
                                    setEditingCategory(null);
                                    setNewCategoryTitle('');
                                    setNewCategoryDisplayOrder('');
                                    setError('');
                                }}
                                className="lut-btn-pine"
                            >
                                + Добавить категорию
                            </button>
                            <p className="mt-2 text-sm text-bark-500">
                                Порядок показа в меню/каталоге: меньше значение — выше категория.
                            </p>
                        </div>
                        {tableWrap(
                            <>
                                <thead className="bg-cream-100/80 text-left text-xs font-semibold uppercase tracking-wide text-bark-600">
                                    <tr>
                                        <th className="px-4 py-3">ID</th>
                                        <th className="px-4 py-3">Название</th>
                                        <th className="w-20 px-4 py-3">Порядок</th>
                                        <th className="px-4 py-3">Действия</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-stone-100 bg-white">
                                    {categories.map((category, idx) => (
                                        <tr key={category.id ?? `cat-${idx}`}>
                                            <td className="px-4 py-3">{category.id}</td>
                                            <td className="px-4 py-3 font-medium">{category.title}</td>
                                            <td className="w-20 px-4 py-3 tabular-nums text-bark-700">
                                                {category.displayOrder ?? 0}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-wrap gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setShowCategoryModal(false);
                                                            setEditingCategory({
                                                                id: category.id,
                                                                title: category.title,
                                                                displayOrder: category.displayOrder ?? 0,
                                                            });
                                                            setNewCategoryTitle(category.title || '');
                                                            setNewCategoryDisplayOrder(
                                                                String(category.displayOrder ?? 0),
                                                            );
                                                            setError('');
                                                        }}
                                                        className="text-sm font-medium text-pine-700 hover:underline"
                                                    >
                                                        Изменить
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteCategory(category.id)}
                                                        className="text-sm text-red-700 hover:underline"
                                                    >
                                                        Удалить
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </>
                        )}
                    </>
                )}
            </div>

            <ItemCreation show={itemForm} onHide={() => {setItemForm(false); loadData();}}/>

            <ItemUpdate
                oldItem={editingItem}
                show={!!editingItem}
                onHide={() => setEditingItem(null)}
                onSaved={loadData}
            />

            {showCategoryModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <button
                        type="button"
                        className="absolute inset-0 bg-bark-900/40 backdrop-blur-sm"
                        aria-label="Закрыть"
                        onClick={() => {
                            setShowCategoryModal(false);
                            setError('');
                            setNewCategoryTitle('');
                            setNewCategoryDisplayOrder('');
                        }}
                    />
                    <div className="relative w-full max-w-md rounded-3xl border border-stone-200/80 bg-white p-6 shadow-2xl">
                        <h2 className="text-lg font-semibold text-bark-900">Добавить категорию</h2>
                        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
                        <label className="lut-label mt-4 block">Название</label>
                        <input
                            type="text"
                            className="lut-input"
                            placeholder="Введите название"
                            value={newCategoryTitle}
                            onChange={(e) => setNewCategoryTitle(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
                        />
                        <label className="lut-label mt-4 block">Порядок показа</label>
                        <input
                            type="number"
                            step="1"
                            className="lut-input"
                            placeholder="Оставьте пустым — будет в конце"
                            value={newCategoryDisplayOrder}
                            onChange={(e) => setNewCategoryDisplayOrder(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
                        />
                        <p className="mt-1 text-xs text-bark-500">
                            Меньше значение — выше категория в меню и каталоге.
                        </p>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                className="lut-btn-ghost"
                                onClick={() => {
                                    setShowCategoryModal(false);
                                    setError('');
                                    setNewCategoryTitle('');
                                    setNewCategoryDisplayOrder('');
                                }}
                            >
                                Отмена
                            </button>
                            <button type="button" className="lut-btn-pine" onClick={handleCreateCategory}>
                                Создать
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {editingCategory && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <button
                        type="button"
                        className="absolute inset-0 bg-bark-900/40 backdrop-blur-sm"
                        aria-label="Закрыть"
                        onClick={() => {
                            setEditingCategory(null);
                            setError('');
                            setNewCategoryTitle('');
                            setNewCategoryDisplayOrder('');
                        }}
                    />
                    <div className="relative w-full max-w-md rounded-3xl border border-stone-200/80 bg-white p-6 shadow-2xl">
                        <h2 className="text-lg font-semibold text-bark-900">Изменить категорию</h2>
                        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
                        <label className="lut-label mt-4 block">Название</label>
                        <input
                            type="text"
                            className="lut-input"
                            placeholder="Введите название"
                            value={newCategoryTitle}
                            onChange={(e) => setNewCategoryTitle(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleUpdateCategory()}
                        />
                        <label className="lut-label mt-4 block">Порядок показа</label>
                        <input
                            type="number"
                            step="1"
                            className="lut-input"
                            placeholder="Целое число"
                            value={newCategoryDisplayOrder}
                            onChange={(e) => setNewCategoryDisplayOrder(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleUpdateCategory()}
                        />
                        <p className="mt-1 text-xs text-bark-500">
                            Меньше значение — выше категория в меню и каталоге.
                        </p>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                className="lut-btn-ghost"
                                onClick={() => {
                                    setEditingCategory(null);
                                    setError('');
                                    setNewCategoryTitle('');
                                    setNewCategoryDisplayOrder('');
                                }}
                            >
                                Отмена
                            </button>
                            <button type="button" className="lut-btn-pine" onClick={handleUpdateCategory}>
                                Сохранить
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Admin;
